import {injectable} from 'inversify';
import {mirana} from '@wize/mirana-proto/mirana-proto';
import axios from 'restyped-axios';
import {ACCESS_TOKEN} from '../utils/constants';
import naxios from 'axios';

import {IngresoRequest, IngresoResponse, MiranaApi, UserDetails,} from '../api/mirana-api';
import {lazyInject} from '../utils/inversify-config';

const apiURL = `${process.env.REACT_APP_API_URL}/api`

const client = axios.create<MiranaApi>({
    baseURL: apiURL
});

const request = (options): Promise<any> => {
    if (localStorage.getItem(ACCESS_TOKEN)) {
        // tslint:disable-next-line
        client.defaults.headers.common['Authorization'] = 'Bearer ' + localStorage.getItem(ACCESS_TOKEN);
    }
    return new Promise<any>((resolve, reject) => {
        client.request(options).then((response) => {
            return resolve(response.data);
        }).catch((error) =>
            reject(error)
        );
    });
};

@injectable()
export class MiranaServiceClient {

    @lazyInject('REDUX_STORE')
    public store;

    public async ingreso(ingresoRequest: IngresoRequest): Promise<IngresoResponse> {
        return request({
            url: '/auth',
            method: 'POST',
            data: ingresoRequest
        });
    }

    public async getCurrentUser(): Promise<UserDetails> {
        if (!localStorage.getItem(ACCESS_TOKEN)) {
            return Promise.reject('No existe access token');
        }
        return request({
            url: '/usuario/me',
            method: 'GET',
        });
    }

    public async obtenerFuerzasVenta(): Promise<mirana.FuerzaVentaList> {
        return request({
            url: '/fuerzas-venta',
            method: 'GET',
        });
    }

    public async filtrarMesas(fuerzaVentaId?: string): Promise<mirana.MesaList> {
        return request({
            url: '/mesas',
            method: 'GET',
            params: {
                fuerzaVentaId
            }
        });
    }

    public async filtrarRutas(fuerzaVentaId?: string, mesaId?: string): Promise<mirana.RutaList> {
        return request({
            url: '/rutas',
            method: 'GET',
            params: {
                fuerzaVentaId,
                mesaId
            }
        });
    }

    public async obtenerRutasDespacho(): Promise<mirana.RutaDespachoList> {
        return request({
            url: '/rutas-despacho',
            method: 'GET',
        });
    }

    public async obtenerMotivosAnulacion(): Promise<mirana.MotivoAnulacionList> {
        return request({
            url: 'motivos-anulacion',
            method: 'GET',
        });
    }

    public async filtrarRechazos(fechaInicio: string, fechaFin: string, fuerzaVentaId?: string, mesaId?: string,
                                 rutaId?: string, rutaDespachoId?: string, motivoAnulacionId?: string): Promise<mirana.FiltrarRechazoResponse> {
        return request({
            url: '/rechazos',
            method: 'GET',
            params: {
                'fecha_inicio': fechaInicio,
                'fecha_fin': fechaFin,
                'fuerza_venta_id': fuerzaVentaId,
                'mesa_id': mesaId,
                'ruta_id': rutaId,
                'ruta_despacho_id': rutaDespachoId,
                'motivo_anulacion_id': motivoAnulacionId
            }
        });
    }

    public async descargarRechazos(fechaInicio: string, fechaFin: string, fuerzaVentaId?: string, mesaId?: string,
                                   rutaId?: string, rutaDespachoId?: string, motivoAnulacionId?: string, tipo: string = 'excel'): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/rechazos/archivo`,
                method: 'GET',
                responseType: 'blob',
                params: {
                    'fecha_inicio': fechaInicio,
                    'fecha_fin': fechaFin,
                    'fuerza_venta_id': fuerzaVentaId,
                    'mesa_id': mesaId,
                    'ruta_id': rutaId,
                    'ruta_despacho_id': rutaDespachoId,
                    'motivo_anulacion_id': motivoAnulacionId,
                    tipo
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                    + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async descargarCelularesZonas(periodo?: string, fecha?: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/celulares/territorio/archivo`,
                method: 'GET',
                responseType: 'blob',
                params: {
                    periodo,
                    fecha
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                        + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async descargarCelularesPdvs(periodo?: string, fecha?: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/celulares/pdv/archivo`,
                method: 'GET',
                responseType: 'blob',
                params: {
                    periodo,
                    fecha
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                        + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async filtrarStock(fecha: string): Promise<mirana.FiltrarStockResponse> {
        return request({
            url: '/stock',
            method: 'GET',
            params: {
                fecha
            }
        });
    }

    public async descargarStock(fecha: string, tipo: string = 'excel'): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/stock/archivo`,
                method: 'GET',
                responseType: 'blob',
                params: {
                    fecha,
                    tipo
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                    + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async filtrarIndicadores(periodo: string): Promise<mirana.IndicadoresList> {
        return request({
            url: '/tdos/indicadores',
            method: 'GET',
            params: {
                periodo
            }
        });
    }

    public async obtenerPeriodosTdos(): Promise<string[]> {
        return request({
            url: '/tdos/periodos',
            method: 'GET'
        });
    }

    public async getCelularZonaPeriodos(): Promise<string[]> {
        return request({
            url: '/celulares/territorio/periodos',
            method: 'GET'
        });
    }

    public async obtenerPeriodosVisicoolers(fuerzaVentaId: string): Promise<string[]> {
        return request({
            url: '/visicoolers/periodos',
            method: 'GET',
            params: {
                fuerzaVentaId
            }
        });
    }

    public async descargarTdos(periodo: string, tipo: string = 'excel'): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/tdos/archivo`,
                method: 'GET',
                responseType: 'blob',
                params: {
                    periodo,
                    tipo
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                    + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async filtrarCoberturas(periodo: string): Promise<mirana.CoberturasList> {
        return request({
            url: '/coberturas',
            method: 'GET',
            params: {
                periodo
            }
        });
    }

    public async descargarCoberturas(periodo: string, tipo: string = 'excel'): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/coberturas/archivo`,
                method: 'GET',
                responseType: 'blob',
                data: JSON.stringify(request),
                params: {
                    periodo,
                    tipo
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                    + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async obtenerPeriodosCoberturas(): Promise<string[]> {
        return request({
            url: '/coberturas/periodos',
            method: 'GET'
        });
    }

    public async descargarPrioritarios(fuerzaVentaId: string, mesaId?: string, rutaId?: string, tipo: string = 'excel'): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/prioritarios/archivo`,
                method: 'GET',
                responseType: 'blob',
                params: {
                    'fuerza_venta_id': fuerzaVentaId,
                    'mesa_id': mesaId,
                    'ruta_id': rutaId,
                    'tipo': tipo
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                    + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async obtenerCarteraDetalle(fuerzaVentaId: string): Promise<mirana.CarteraDetalle> {
        return request({
            url: '/cartera-total',
            method: 'GET',
            params: {
                'fuerza_venta_id': fuerzaVentaId
            }
        });
    }

    public async obtenerPrioritariosLineas(fuerzaVentaId: string): Promise<mirana.PrioritarioLineaList> {
        return request({
            url: '/prioritarios/lineas',
            method: 'GET',
            params: {
                'fuerza_venta_id': fuerzaVentaId
            }
        });
    }

    public async actualizarPrioritariosLineas(fuerzaVentaId: string, data: mirana.PrioritarioLineaList): Promise<void> {
        return request({
            url: '/prioritarios/lineas',
            method: 'PUT',
            data,
            params: {
                'fuerza_venta_id': fuerzaVentaId
            }
        });
    }

    public async filtrarVisicoolers(fuerzaVentaId: string, periodo: string, keyword?: string, page?: number): Promise<mirana.BuscarVisicoolersResponse> {
        return request({
            url: '/visicoolers',
            method: 'GET',
            params: {
                fuerzaVentaId,
                periodo,
                keyword,
                page
            }
        });
    }

    public async retirarVisicooler(clienteId: string, fuerzaVentaId: string, modeloId: number): Promise<void> {
        return request({
            url: '/visicooler',
            method: 'DELETE',
            params: {
                clienteId, fuerzaVentaId, modeloId
            }
        });
    }

    public async actualizarVisicooler(clienteId: string, modeloId: number, fuerzaVentaId: string, estado: number): Promise<void> {
        return request({
            url: '/visicooler',
            method: 'PUT',
            params: {
                clienteId, modeloId, fuerzaVentaId, estado
            }
        });
    }

    public async filtrarVisicoolerModelos(fuerzaVentaId: string, estado?: number): Promise<mirana.VisicoolerModeloResponse> {
        return request({
            url: '/visicoolers/modelos',
            method: 'GET',
            params: {
                estado,
                fuerzaVentaId
            }
        });
    }

    public async actualizarModelo(body: mirana.IVisicoolerModelo): Promise<void> {
        return request({
            url: '/visicoolers/modelos',
            method: 'PUT',
            data: body
        });
    }

    public async filtrarClientesSinVisicooler(fuerzaVentaId: string, keyword?: string, page?: number): Promise<mirana.BuscarVisicoolersResponse> {
        return request({
            url: '/visicoolers/clientes',
            method: 'GET',
            params: {
                fuerzaVentaId,
                keyword,
                page
            }
        });
    }

    public async asignarVisicooler(clienteId: string, modeloId: number, fuerzaVentaId: string): Promise<void> {
        return request({
            url: '/visicooler',
            method: 'POST',
            params: {
                clienteId,
                modeloId,
                fuerzaVentaId
            }
        });
    }

    public async descargarVisicoolers(fuerzaVentaId: string, periodo: string, tipo: string = 'excel'): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/visicoolers/archivo`,
                method: 'GET',
                responseType: 'blob',
                params: {
                    tipo,
                    periodo,
                    fuerzaVentaId
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                    + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async filtrarStockSublineas(fecha: string, fuerzaVentaId?: string): Promise<mirana.FiltrarStockProductoResponse> {
        return request({
            url: '/stock/sublineas',
            method: 'GET',
            params: {
                fecha,
                'fuerza_venta_id': fuerzaVentaId
            }
        });
    }

    public async descargarStockSublineas(fecha: string, fuerzaVentaId?: string, tipo: string = 'excel'): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/stock/sublineas/archivo`,
                method: 'GET',
                responseType: 'blob',
                params: {
                    fecha,
                    'fuerza_venta_id': fuerzaVentaId,
                    tipo
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                    + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async obtenerAvanceAnualCategorias(): Promise<string[]> {
        return request({
            url: '/avance-anual/categorias',
            method: 'GET'
        });
    }

    public async filtrarAvanceAnualLineas(categorias?: string[]): Promise<string[]> {
        return request({
            url: '/avance-anual/lineas',
            method: 'GET',
            params: {
                categorias
            }
        });
    }

    public async filtrarAvanceAnual(categorias?: string[], lineas?: string[]): Promise<mirana.AvanceAnualList> {
        return request({
            url: '/avance-anual',
            method: 'GET',
            params: {
                categorias,
                lineas
            }
        });
    }

    public async descargarAvanceAnual(categorias?: string[], lineas?: string[], tipo: string = 'excel'): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/avance-anual/archivo`,
                method: 'GET',
                responseType: 'blob',
                data: JSON.stringify(request),
                params: {
                    categorias,
                    lineas,
                    tipo
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                    + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async descargarPlantillaVcCuotas(fuerzaVentaId?: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/visicoolers/cuotas/plantilla`,
                method: 'GET',
                responseType: 'blob',
                data: JSON.stringify(request),
                params: {
                    fuerzaVentaId
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                    + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async descargarPlantillaCelularesZonas(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/celulares/territorio/plantilla`,
                method: 'GET',
                responseType: 'blob',
                data: JSON.stringify(request),
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                        + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    public async descargarPlantillaTiendaPago(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            naxios({
                url: `${apiURL}/prioritarios/tiendapago/plantilla`,
                method: 'GET',
                responseType: 'blob',
                data: JSON.stringify(request),
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
                    'Accept': 'application/ms-excel'
                        + '.spreadsheetml.sheet',
                },
            }).then((response) => {
                resolve(response);
            }).catch((error) => {
                reject(error);
            });
        });
    }
}

export default MiranaServiceClient;



// WEBPACK FOOTER //
// ./src/middleware/mirana-client.ts